import { useState, useEffect, useRef, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle, PlusCircle, ChevronDown } from 'lucide-react';
import { removeAccents } from '../../utils/searchUtils';

interface DropdownMultiTypeaheadProps<T> {
  selected: T[];
  onChange: (selected: T[]) => void;
  fetchOptions?: (query: string) => Promise<T[]>;
  options?: T[];
  onSearchChange?: (query: string) => void;
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string | number;
  placeholder?: string;
  className?: string;
  error?: boolean;
  hideSelected?: boolean;
  disabled?: boolean;
  inputId?: string;
  ariaDescribedBy?: string;
  onCreate?: (query: string) => Promise<void> | void;
  groupBy?: (item: T) => string;
  inputMode?: 'search' | 'select';
}

export function DropdownMultiTypeahead<T>({
  selected,
  onChange,
  fetchOptions,
  options,
  onSearchChange,
  getOptionLabel,
  getOptionValue,
  placeholder,
  className = '',
  error = false,
  hideSelected = false,
  disabled = false,
  inputId,
  ariaDescribedBy,
  onCreate,
  groupBy,
  inputMode = 'select',
}: DropdownMultiTypeaheadProps<T>) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.length < 2 && !options) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }
    if (fetchOptions) {
      let active = true;
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const data = await fetchOptions(query);
          if (active) setSuggestions(data);
        } finally {
          if (active) setIsLoading(false);
        }
      };
      fetchData();
      return () => {
        active = false;
      };
    } else if (options && !query) {
      setSuggestions(options);
      setIsLoading(false);
    } else if (options) {
      const filtered = options.filter(item =>
        removeAccents(getOptionLabel(item).toLowerCase()).includes(
          removeAccents(query.toLowerCase())
        )
      );
      setSuggestions(filtered);
      setIsLoading(false);
    }
  }, [query, options, fetchOptions, getOptionLabel]);

  const handleRemove = (item: T) => {
    const newSelected = selected.filter(sel => getOptionValue(sel) !== getOptionValue(item));
    onChange(newSelected);
  };

  const handleSelect = (item: T) => {
    if (!selected.some(sel => getOptionValue(sel) === getOptionValue(item))) {
      const newSelected = [...selected, item];
      onChange(newSelected);
      setQuery('');
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleCreate = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (onCreate && query.length > 2) {
      setIsCreating(true);
      try {
        await onCreate(query);
        setQuery('');
        if (inputRef.current) inputRef.current.focus();
      } finally {
        setIsCreating(false);
      }
    }
  };

  const availableSuggestions = suggestions.filter(
    item => !selected.some(sel => getOptionValue(sel) === getOptionValue(item))
  );

  const groupedSuggestions = groupBy
    ? availableSuggestions.reduce(
        (acc, item) => {
          const group = groupBy(item);
          if (!acc[group]) acc[group] = [];
          acc[group].push(item);
          return acc;
        },
        {} as Record<string, T[]>
      )
    : {};

  const sortedGroups = Object.keys(groupedSuggestions).sort();

  return (
    <div className={`relative ${className}`}>
      {selected.length > 0 && !hideSelected && (
        <div className="flex flex-wrap gap-2 mb-1 overflow-x-auto md:overflow-x-auto md:scrollbar-thin md:scrollbar-thumb-secondary/20 md:space-x-2">
          {selected.map(item => (
            <button
              key={getOptionValue(item)}
              className="flex items-center justify-center w-full px-3 py-1 bg-primary/5 border border-primary/20 rounded-full focus:outline-none font-ambit font-semibold text-primary text-sm gap-1 md:w-auto transition-colors hover:bg-primary/10"
              onClick={() => handleRemove(item)}
              aria-label={t('remove')}
            >
              <span>{getOptionLabel(item)}</span>
              <XCircle className="w-4 h-4 ml-1 stroke-primary/50 hover:stroke-primary" />
            </button>
          ))}
        </div>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          id={inputId}
          className={`w-full min-h-[2.75rem] px-4 py-2 mt-2 font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] rounded-2xl border transition-colors placeholder:text-secondary/40 focus:outline-none cursor-pointer ${
            error
              ? 'border-[#F04438] bg-[#FEE4E2]'
              : 'border-primary/20 bg-[#F0F5F7] focus:border-primary focus:ring-2 focus:ring-primary/10'
          } ${disabled || isCreating ? 'opacity-50 cursor-not-allowed' : ''}`}
          value={query}
          onChange={e => {
            const value = e.target.value;
            setQuery(value);
            if (onSearchChange) {
              onSearchChange(value);
            }
            setShowSuggestions(true);
          }}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          style={{ paddingRight: '2.5rem' }}
          disabled={disabled || isCreating}
          aria-invalid={error || undefined}
          aria-describedby={ariaDescribedBy}
        />
        {((inputMode === 'select' && !!options) || (inputMode === 'search' && !!fetchOptions)) &&
          !query && (
            <button
              type="button"
              className="absolute right-3 inset-y-0 flex items-center mt-2"
              onMouseDown={e => {
                if (inputMode === 'select') {
                  e.preventDefault();
                  setShowSuggestions(s => !s);
                  if (inputRef.current) inputRef.current.focus();
                }
              }}
              aria-label="Toggle suggestions"
            >
              <ChevronDown
                className={`w-4 h-4 text-secondary/40 transition-transform duration-200 ${showSuggestions ? 'rotate-180' : ''}`}
              />
            </button>
          )}
      </div>

      {showSuggestions && (query.length > 0 || (inputMode === 'select' && !!options)) && (
        <ul className="absolute z-10 w-full mt-1 bg-white border border-primary/15 max-h-48 overflow-y-auto rounded-2xl shadow-[0_8px_24px_rgba(13,45,57,0.12)]">
          {isLoading && (
            <li className="px-4 py-2.5 text-secondary/40 italic font-ambit text-[0.9375rem]">
              {t('loading')}
            </li>
          )}

          {!isLoading && availableSuggestions.length === 0 && (
            <>
              {(!onCreate || query.length <= 2) && (
                <li className="px-4 py-2.5 text-secondary/40 italic text-center font-ambit text-[0.9375rem]">
                  {t('noResults')}
                </li>
              )}

              {onCreate && query.length > 2 && (
                <li
                  className="px-4 py-2.5 cursor-pointer border-b border-primary/10 last:border-b-0 hover:bg-primary/5 font-ambit text-[0.9375rem] text-secondary transition-colors flex items-center gap-2"
                  onMouseDown={handleCreate}
                >
                  {isCreating ? (
                    <span className="italic text-secondary/40">
                      {t('common:form_actions.saving')}...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 bg-primary/5 px-2 py-1 rounded-xl border border-primary/15 hover:border-primary transition-colors">
                      <PlusCircle className="w-4 h-4 text-primary" />
                      <span>
                        {t('common:form_actions.add', { ns: 'common' })} &ldquo;{query}&rdquo;
                      </span>
                    </span>
                  )}
                </li>
              )}
            </>
          )}

          {!isLoading &&
            (groupBy
              ? sortedGroups.map(group => (
                  <Fragment key={group}>
                    <li className="px-4 py-1.5 text-xs font-bold text-secondary/40 bg-secondary/5 uppercase tracking-wider sticky top-0 border-b border-primary/10">
                      {group}
                    </li>
                    {groupedSuggestions[group].map(item => (
                      <li
                        key={getOptionValue(item)}
                        className="px-4 py-2.5 cursor-pointer border-b border-primary/10 last:border-b-0 hover:bg-primary/5 hover:text-primary font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary transition-colors pl-6"
                        onMouseDown={() => handleSelect(item)}
                      >
                        {getOptionLabel(item)}
                      </li>
                    ))}
                  </Fragment>
                ))
              : availableSuggestions.map(item => (
                  <li
                    key={getOptionValue(item)}
                    className="px-4 py-2.5 cursor-pointer border-b border-primary/10 last:border-b-0 hover:bg-primary/5 hover:text-primary font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary transition-colors"
                    onMouseDown={() => handleSelect(item)}
                  >
                    {getOptionLabel(item)}
                  </li>
                )))}
        </ul>
      )}
    </div>
  );
}
